import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Printer, Package, AlertTriangle, CheckCircle, History, RefreshCw, Usb, Settings, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { usePricingChannels } from '@/hooks/usePricingChannels';
import { PricingTypePrompt } from '@/components/PricingTypePrompt';
import * as printerApi from '@/services/printerApi';

const SFGMainLabelPrinting = () => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  const staffInputRef = useRef(null);
  // Staff ID first (it is cleared after every scan, printed or failed), then the bundle box.
  const ratePendingRef = useRef(false); // "which pricing type" prompt open → keep the Staff ID for the re-print
  const focusNext = () => {
    const staffEmpty = staffInputRef.current && !staffInputRef.current.value.trim();
    (staffEmpty ? staffInputRef.current : scanInputRef.current)?.focus();
  };
  const isAdmin = (() => {
    try {
      const role = (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase();
      return ['admin', 'super_admin', 'site admin'].includes(role);
    } catch { return false; }
  })();
  const [adminOverride, setAdminOverride] = useState(false);
  
  // State
  const [bundleCode, setBundleCode] = useState('');
  const [staffId, setStaffId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [lastPrintedBundle, setLastPrintedBundle] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [printHistory, setPrintHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Pricing Type State
  const [pricingType, setPricingType] = useState('TN'); // Default to TN
  const availablePricingTypes = usePricingChannels();
  const [ratePrompt, setRatePrompt] = useState(null); // SKU has no rate in the chosen pricing type → ask which one
  
  // Combo/Box Sticker Alert
  const [showComboAlert, setShowComboAlert] = useState(false);
  const [pendingPrintData, setPendingPrintData] = useState(null);
  const [boxPrinterDevice, setBoxPrinterDevice] = useState(null); // Second printer for box labels
  
  // Settings
  const [templates, setTemplates] = useState([]);
  const [printerModel, setPrinterModel] = useState('TSC_TE210');
  const [copiesPerLabel, setCopiesPerLabel] = useState(1);
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  
  // Line spacing multiplier
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);
  
  // Print statistics for current session
  const [sessionStats, setSessionStats] = useState({ printed: 0, failed: 0 });
  
  // Printer configurations
  const PRINTER_CONFIGS = {
    TSC_TE210: {
      name: 'TSC TE210 (Desktop)',
      dpi: 203,
      speed: 4,
      density: 8,
      vendorId: 0x1203
    },
    TSC_MH240: {
      name: 'TSC MH240 (Industrial)',
      dpi: 203,
      speed: 6,
      density: 10,
      vendorId: 0x1203
    }
  };
  
  useEffect(() => {
    scanInputRef.current?.focus();
    setUsbSupported(printerApi.isWebUSBSupported());
    fetchTemplates();
    fetchPrintSettings();
    
    // Auto-focus interval to keep focus on bundle scan input
    const focusInterval = setInterval(() => {
      // Only auto-focus if not processing, no dialogs open, and input exists
      if (!isProcessing && !showHistory && !showSettings && !showComboAlert && !ratePrompt && scanInputRef.current) {
        const activeElement = document.activeElement;
        // Don't steal focus if user is typing in another input
        if (!activeElement || activeElement.tagName !== 'INPUT' || activeElement === scanInputRef.current) {
          focusNext();
        }
      }
    }, 500);
    
    return () => clearInterval(focusInterval);
  }, [isProcessing, showHistory, showSettings, showComboAlert, ratePrompt]);
  
  // Additional effect to focus after processing completes
  useEffect(() => {
    if (!isProcessing && !showComboAlert) {
      setTimeout(focusNext, 100);
    }
  }, [isProcessing, showComboAlert]);
  
  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/main-labels/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  const fetchPrintSettings = async () => {
    try {
      const response = await apiClient.get('/print-settings');
      if (response.data.settings?.arial_line_spacing_multiplier) {
        setArialLineSpacingMultiplier(response.data.settings.arial_line_spacing_multiplier);
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
    }
  };
  
  // Find template ID by template name from item_master
  const findTemplateByName = (templateName) => {
    if (!templateName || !templates.length) return null;
    
    // Find template that matches the name (case-insensitive)
    const template = templates.find(t => 
      t.template_name?.toLowerCase() === templateName.toLowerCase() ||
      t.template_name?.toLowerCase().includes(templateName.toLowerCase()) ||
      templateName.toLowerCase().includes(t.template_name?.toLowerCase())
    );
    
    return template || null;
  };
  
  // Generate TSPL commands
  const generateTsplFromLabelData = (labelData, template, usnCode, mrpFormatted, displayUsn = null, printerConfig = null, templateType = null, pairNumber = null) => {
    const config = printerConfig || PRINTER_CONFIGS.TSC_TE210;
    const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
    const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
    const usnDisplay = displayUsn || usnCode;
    const fields = template?.fields || {};
    const tplType = templateType || template?.template_type || '';
    const isBoxLabel = tplType?.toLowerCase().includes('box') || pairNumber;
    
    const getField = (key, defaultX = 0, defaultY = 0, defaultFontSize = 2, defaultSize = 4) => {
      const field = fields[key] || {};
      return {
        x: field.x ?? defaultX,
        y: field.y ?? defaultY,
        font_size: field.font_size ?? defaultFontSize,
        bold: field.bold ?? false,
        size: field.size ?? defaultSize,
        text: field.text || ''
      };
    };
    
    const generateTextCmd = (x, y, fontSize, text) => {
      const arialSize = fontSize || 10.5;
      return `TEXT ${x},${y},"ARIALB.TTF",0,${arialSize},${arialSize},"${text}"\r\n`;
    };
    
    let tspl = '';
    
    tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
    tspl += `GAP 3 mm, 0 mm\r\n`;
    tspl += `SPEED ${config.speed}\r\n`;
    tspl += `DENSITY ${config.density}\r\n`;
    tspl += `DIRECTION 1,0\r\n`;
    tspl += `REFERENCE 0,0\r\n`;
    tspl += `OFFSET 0 mm\r\n`;
    tspl += `SET PEEL OFF\r\n`;
    tspl += `SET CUTTER OFF\r\n`;
    tspl += `SET PARTIAL_CUTTER OFF\r\n`;
    tspl += `CLS\r\n`;
    
    // QUALITY
    const qualityLabel = getField('quality_label', 16, 16, 10.5);
    const qualityValue = getField('quality_value', 160, 16, 14.0);
    if (labelData.quality) {
      tspl += generateTextCmd(qualityLabel.x, qualityLabel.y, qualityLabel.font_size, qualityLabel.text || 'QUALITY');
      tspl += generateTextCmd(qualityValue.x, qualityValue.y, qualityValue.font_size, labelData.quality);
    }
    
    // COLOR
    if (labelData.color) {
      const colorLabel = getField('color_label', 16, 56, 10.5);
      const colorValue = getField('color_value', 160, 56, 10.5);
      tspl += generateTextCmd(colorLabel.x, colorLabel.y, colorLabel.font_size, colorLabel.text || 'COLOR');
      tspl += generateTextCmd(colorValue.x, colorValue.y, colorValue.font_size, labelData.color);
    }
    
    // CONTENT
    const contentLabel = getField('content_label', 16, 96, 10.5);
    const contentValue = getField('content_value', 160, 96, 14.0);
    if (labelData.content1) {
      let contentParts = [labelData.content1];
      if (labelData.content2) contentParts.push(labelData.content2);
      if (labelData.content3) contentParts.push(labelData.content3);
      const content = contentParts.join('    ');
      tspl += generateTextCmd(contentLabel.x, contentLabel.y, contentLabel.font_size, contentLabel.text || 'CONTENT');
      tspl += generateTextCmd(contentValue.x, contentValue.y, contentValue.font_size, content);
    }
    
    // SIZE
    const sizeLabel = getField('size_label', 16, 136, 10.5);
    const sizeValue = getField('size_value', 160, 136, 10.5);
    let sizeParts = [];
    if (labelData.size1) sizeParts.push(labelData.size1);
    if (labelData.size2) sizeParts.push(labelData.size2);
    if (labelData.size3) sizeParts.push(labelData.size3);
    const size = sizeParts.length > 0 ? sizeParts.join('    ') : (labelData.size_from_rates || '');
    if (size) {
      tspl += generateTextCmd(sizeLabel.x, sizeLabel.y, sizeLabel.font_size, sizeLabel.text || 'SIZE');
      tspl += generateTextCmd(sizeValue.x, sizeValue.y, sizeValue.font_size, size);
    }
    
    // DESC5 Bold for combo
    if (labelData.desc5_bold && tplType?.includes('combo')) {
      const desc5Field = getField('desc5_bold', 400, 56, 14.0);
      tspl += generateTextCmd(desc5Field.x, desc5Field.y, desc5Field.font_size, labelData.desc5_bold);
    }
    
    // QTY (value configurable via template — falls back to label data / default)
    const qtyField = getField('qty', 16, 176, 10.5);
    const qtyVal = template?.fields?.qty?.value;
    const qtyText = (qtyVal !== undefined && qtyVal !== null && qtyVal !== '')
      ? `QTY : ${qtyVal} N`
      : (labelData.qty || 'QTY : 1 N');
    tspl += generateTextCmd(qtyField.x, qtyField.y, qtyField.font_size, qtyText);
    
    // MRP
    const mrpLabel = getField('mrp_label', 200, 176, 10.5);
    const mrpValue = getField('mrp_value', 280, 166, 18.0);
    tspl += generateTextCmd(mrpLabel.x, mrpLabel.y, mrpLabel.font_size, mrpLabel.text || 'MRP :');
    tspl += generateTextCmd(mrpValue.x, mrpValue.y, mrpValue.font_size, mrpFormatted);
    
    // Tax Notice
    const taxNotice = getField('tax_notice', 280, 210, 7.5);
    tspl += generateTextCmd(taxNotice.x, taxNotice.y, taxNotice.font_size, '(INCL. OF ALL TAXES)');
    
    // MADE IN INDIA
    const madeInIndia = getField('made_in_india', 16, 250, 10.5);
    tspl += generateTextCmd(madeInIndia.x, madeInIndia.y, madeInIndia.font_size, 'MADE IN INDIA');
    
    // PAIR NUMBER - Display BESIDE the USN text (not at top center)
    // QR codes contain ONLY the plain USN/barcode (no pair number embedded)
    // The pair number will be added to the USN display text in the qr1_text section below
    
    // QR Code 1 (USN)
    const qr1 = getField('qr1', 16, 290, 10.5, 5);
    const qr1Text = getField('qr1_text', 16, 410, 10.5);
    if (usnCode) {
      tspl += `QRCODE ${qr1.x},${qr1.y},L,${qr1.size},A,0,"${usnCode}"\r\n`;
      tspl += generateTextCmd(qr1Text.x, qr1Text.y, qr1Text.font_size, usnDisplay);
    }
    
    // QR Code 2 (Barcode)
    const qr2 = getField('qr2', 200, 290, 10.5, 5);
    const qr2Text = getField('qr2_text', 200, 410, 10.5);
    if (labelData.qr2_code) {
      tspl += `QRCODE ${qr2.x},${qr2.y},L,${qr2.size},A,0,"${labelData.qr2_code}"\r\n`;
      tspl += generateTextCmd(qr2Text.x, qr2Text.y, qr2Text.font_size, labelData.qr2_code);
    }
    
    // Consumer Notice
    const consumerNotice = getField('consumer_notice', 16, 460, 7.5);
    const actualFontSize = consumerNotice.font_size || 7.5;
    const lineHeight = Math.ceil(actualFontSize * arialLineSpacingMultiplier);
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y, consumerNotice.font_size, 'Note: Consumer is free to open and');
    tspl += generateTextCmd(consumerNotice.x, consumerNotice.y + lineHeight, consumerNotice.font_size, 'inspect the product before buying it');
    
    tspl += `PRINT 1,1\r\n`;
    
    return tspl;
  };
  
  // Connect USB printer (main printer for product labels)
  const connectUSBPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'} (Product Labels)`);
      return device;
    } catch (error) {
      console.error('Error connecting USB:', error);
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  // Connect second USB printer for box labels
  const connectBoxPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setBoxPrinterDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'} (Box Labels)`);
      return device;
    } catch (error) {
      console.error('Error connecting box printer:', error);
      toast.error('Failed to connect box label printer. Please try again.');
      return null;
    }
  };
  
  // Execute the actual print job
  const executePrint = async (bundleData, device, printType = 'product', pt = pricingType) => {
    try {
      const printerConfig = PRINTER_CONFIGS[printerModel];
      
      let allCommands = '';
      let totalLabels = 0;
      let skippedUsns = 0;
      const skippedReasons = {};  // reason -> count, surfaces in the final error toast
      
      // Get all unique barcodes from USNs, plus the bundle's main item_barcode as fallback
      const barcodes = [...new Set([
        ...(bundleData.usns || []).map(usn => usn.barcode),
        bundleData.item_barcode  // Always include main barcode for fallback
      ].filter(Boolean))];

      // DIAGNOSTIC: log bundle structure so production issues are traceable from the browser console.
      console.log('[PRINT] bundle:', {
        bundle_code: bundleData.bundle_code,
        item_barcode: bundleData.item_barcode,
        usn_count: bundleData.usns?.length || 0,
        usns_with_barcode: (bundleData.usns || []).filter((u) => u.barcode).length,
        usns_without_barcode: (bundleData.usns || []).filter((u) => !u.barcode).length,
        unique_barcodes_count: barcodes.length,
      });

      if ((bundleData.usns || []).length === 0) {
        throw new Error(
          `Bundle ${bundleData.bundle_code || ''} returned 0 USNs. Check Bundle Receipt / all_usn_details.`
        );
      }
      if (barcodes.length === 0) {
        throw new Error(
          `Bundle ${bundleData.bundle_code || ''} has ${bundleData.usns.length} USN(s) but no valid barcodes — all USNs are missing fg_barcode and item_barcode is empty. Check item_master and all_usn_details for this bundle.`
        );
      }
      
      // Fetch all label data in ONE batch request (much faster!)
      setProcessingStatus(`Fetching label data for ${barcodes.length} items...`);
      const batchResponse = await apiClient.post('/main-labels/preview-batch', {
        barcodes: barcodes,
        pricing_type: pt,
        template_id: templates[0]?.template_id,
        fallback_barcode: bundleData.item_barcode  // Provide explicit fallback
      });
      // SKU without a rate in this pricing type would print blank stickers → stop and ask which pricing type to use.
      const used = new Set((bundleData.usns || []).map((u) => u.barcode || bundleData.item_barcode));
      const gaps = (batchResponse.data.missing_rate || []).filter((b) => used.has(b));
      if (gaps.length) {
        ratePendingRef.current = true;
        setRatePrompt({ code: bundleData.bundle_code, pt, barcodes: gaps, options: batchResponse.data.rate_options || [] });
        return false;
      }
      
      const labelDataMap = batchResponse.data.results || {};
      const defaultTemplate = batchResponse.data.template;
      
      // Generate commands for each USN using cached data
      const labelTypeText = printType === 'box' ? 'box' : 'product';
      setProcessingStatus(`Generating ${bundleData.usns?.length || 0} ${labelTypeText} labels...`);
      
      for (const usn of bundleData.usns || []) {
        try {
          // Resolve an effective barcode: USN's own → bundle's item_barcode fallback.
          // This rescues bundles where all_usn_details rows for some USNs don't yet
          // carry fg_barcode (e.g., FG-only records) but the bundle itself has a
          // valid item_barcode.
          const effectiveBarcode = usn.barcode || bundleData.item_barcode;
          const cachedData = labelDataMap[effectiveBarcode];
          if (!cachedData) {
            const reason = !effectiveBarcode
              ? 'no-barcode'
              : `no-batch-result(${effectiveBarcode})`;
            skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
            skippedUsns += 1;
            console.warn(
              `[PRINT] Skipping USN ${usn.usn_code}: ${reason}. usn.barcode=${usn.barcode}, item_barcode=${bundleData.item_barcode}`
            );
            continue;
          }
          
          const labelData = cachedData.label_data || {};
          const mrpFormatted = cachedData.mrp_formatted || `Rs.${labelData.mrp || 0}`;
          
          if (printType === 'product') {
            // Generate PRODUCT label (main sticker)
            const templateName = usn.print_template || 'Standard';
            const template = findTemplateByName(templateName) || defaultTemplate;
            
            for (let copy = 0; copy < copiesPerLabel; copy++) {
              allCommands += generateTsplFromLabelData(
                labelData,
                template,
                usn.usn_code,
                mrpFormatted,
                null,
                printerConfig,
                template?.template_type
              );
              totalLabels++;
            }
          } else if (printType === 'box' && usn.box_template) {
            // Generate BOX label only for items with box_template
            const boxTemplate = findTemplateByName(usn.box_template);
            
            // DEBUG: Log pair_number and template matching
            console.log(`[BOX LABEL] USN: ${usn.usn_code}, pair_number: ${usn.pair_number}, box_template: ${usn.box_template}`);
            console.log(`[BOX LABEL] Template found: ${boxTemplate ? boxTemplate.template_name : 'NOT FOUND'}`);
            console.log(`[BOX LABEL] Available templates:`, templates.map(t => t.template_name));
            
            if (boxTemplate) {
              // Create display USN with pair number for box labels
              const displayUsn = usn.pair_number ? `${usn.usn_code} (#${usn.pair_number})` : usn.usn_code;
              console.log(`[BOX LABEL] displayUsn constructed: ${displayUsn}`);
              
              for (let copy = 0; copy < copiesPerLabel; copy++) {
                allCommands += generateTsplFromLabelData(
                  labelData,
                  boxTemplate,
                  usn.usn_code,
                  mrpFormatted,
                  displayUsn,  // Pass USN with pair number for display on box label
                  printerConfig,
                  boxTemplate?.template_type || 'box',
                  usn.pair_number  // Pass pair number separately for prominent display
                );
                totalLabels++;
              }
            } else {
              // FALLBACK: Box template not found, use default template but still print with pair number
              console.warn(`Box template '${usn.box_template}' not found for barcode ${usn.barcode}, using default template`);
              const defaultTemplate = templates[0] || null;  // Use first available template as fallback
              
              if (defaultTemplate) {
                const displayUsn = usn.pair_number ? `${usn.usn_code} (#${usn.pair_number})` : usn.usn_code;
                console.log(`[BOX LABEL FALLBACK] Using default template: ${defaultTemplate.template_name}, displayUsn: ${displayUsn}`);
                
                for (let copy = 0; copy < copiesPerLabel; copy++) {
                  allCommands += generateTsplFromLabelData(
                    labelData,
                    defaultTemplate,
                    usn.usn_code,
                    mrpFormatted,
                    displayUsn,
                    printerConfig,
                    'box',  // Force box type
                    usn.pair_number
                  );
                  totalLabels++;
                }
              }
            }
          }
        } catch (usnError) {
          console.error(`Error generating label for ${usn.usn_code}:`, usnError);
        }
      }
      
      if (!allCommands) {
        if (printType === 'box') {
          toast.warning('No box labels to print for this bundle');
          return;
        }
        // Build a specific diagnostic message from the skipped reasons so
        // the operator (and support) can see WHY nothing printed.
        const reasonSummary = Object.entries(skippedReasons)
          .map(([r, n]) => `${n}×${r}`)
          .join(', ') || 'unknown';
        throw new Error(
          `No printable labels for ${bundleData.bundle_code || 'bundle'} — ` +
          `${skippedUsns}/${bundleData.usns?.length || 0} USN(s) skipped (${reasonSummary}). ` +
          `Check all_usn_details for this bundle's FG barcode.`
        );
      }
      
      // Print via USB
      if (device || printerApi.isNetworkPrintActive()) {
        setProcessingStatus(`Printing ${totalLabels} ${labelTypeText} labels...`);
        await printerApi.sendToUSBPrinter(device, allCommands);
      }
      
      // Mark as printed (only for product labels, or if no product labels were printed)
      if (printType === 'product') {
        setProcessingStatus('Updating status...');
        await apiClient.post('/wms/sfg/print-bundle-labels', {
          bundle_code: bundleData.bundle_code,
          print_method: 'USB',
          staff_id: staffId.trim(),
          admin_override: isAdmin && adminOverride,
        });
      }
      
      // Success!
      setLastPrintedBundle({
        bundle_code: bundleData.bundle_code,
        usn_count: bundleData.usn_count,
        labels_printed: totalLabels,
        templates_used: bundleData.templates_needed,
        label_type: printType,
        timestamp: new Date()
      });
      
      setSessionStats(prev => ({ ...prev, printed: prev.printed + 1 }));
      toast.success(`Printed ${totalLabels} ${labelTypeText} labels for ${bundleData.bundle_code}`);
      return true;
    } catch (error) {
      console.error('Print error:', error);
      setSessionStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      toast.error(error.response?.data?.detail || error.message || 'Print failed');
      return false;
    }
  };
  
  // Main scan handler
  const handleScanAndPrint = async (ptOverride, codeOverride) => {
    const code = (codeOverride || bundleCode).trim();
    const pt = ptOverride || pricingType;
    if (!code) {
      toast.error('Please scan a bundle code');
      return;
    }
    if (!staffId.trim()) {
      toast.error('Enter the Staff ID before scanning the bundle');
      return;
    }
    
    // Check USB device
    let device = usbDevice;
    if (!device) {
      setProcessingStatus('Connecting to USB printer...');
      device = await connectUSBPrinter();
      if (!device) {
        setProcessingStatus('');
        setBundleCode('');
        setPricingType('TN');
        setStaffId('');
        return;
      }
    }
    
    setIsProcessing(true);
    
    try {
      // Step 1: Validate bundle
      setProcessingStatus('Validating bundle...');
      const validateResponse = await apiClient.post(`/wms/sfg/validate-bundle-for-label/${encodeURIComponent(code)}${isAdmin && adminOverride ? '?admin_override=true' : ''}`);
      const bundleData = validateResponse.data;
      
      if (!bundleData.success) {
        throw new Error('Bundle validation failed');
      }
      
      // Step 2: Check if item has box_template (needs box sticker)
      if (bundleData.has_box_template || bundleData.is_combo) {
        // FIRST: Print PRODUCT labels
        setProcessingStatus(`Printing PRODUCT labels for ${bundleData.usn_count} USNs...`);
        if (!(await executePrint(bundleData, device, 'product', pt))) return;
        
        // THEN: Show box sticker alert - pause for user to change sticker roll
        setIsProcessing(false);
        setProcessingStatus('');
        setPendingPrintData({ bundleData, device, pt });
        setShowComboAlert(true);
        return;
      }
      
      // Step 3: Auto-print product labels (no box template, proceed directly)
      setProcessingStatus(`Generating labels for ${bundleData.usn_count} USNs...`);
      await executePrint(bundleData, device, 'product', pt);
      
    } catch (error) {
      console.error('Print error:', error);
      setSessionStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      toast.error(error.response?.data?.detail || error.message || 'Print failed');
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
      setBundleCode('');
      setPricingType('TN');
      if (!ratePendingRef.current) setStaffId('');
    }
  };
  
  // Handle combo confirmation (box sticker changed) - Print BOX labels only
  const handleComboConfirm = async () => {
    setShowComboAlert(false);
    
    if (!pendingPrintData) return;
    
    const { bundleData, pt } = pendingPrintData;
    
    // Use box printer if connected, otherwise prompt to connect
    let printerToUse = boxPrinterDevice;
    if (!printerToUse) {
      toast.info('Please select a printer for box labels');
      printerToUse = await connectBoxPrinter();
      if (!printerToUse) {
        // Re-show the dialog if no printer selected
        setShowComboAlert(true);
        return;
      }
    }
    
    setIsProcessing(true);
    setProcessingStatus(`Generating BOX labels for ${bundleData.usn_count} USNs...`);
    
    try {
      // Print ONLY BOX labels using the box printer
      await executePrint(bundleData, printerToUse, 'box', pt);
    } catch (error) {
      console.error('Print error:', error);
      setSessionStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      toast.error(error.message || 'Print failed');
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
      setBundleCode('');
      setStaffId('');
      setPricingType('TN');
      setPendingPrintData(null);
    }
  };
  
  // Cancel combo print (product labels are already printed)
  const handleComboCancel = () => {
    setShowComboAlert(false);
    setPendingPrintData(null);
    setBundleCode('');
    setStaffId('');
    setPricingType('TN');
  };
  
  const loadPrintHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get('/wms/sfg/label-print-history?limit=50');
      setPrintHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load print history');
    } finally {
      setHistoryLoading(false);
    }
  };
  
  const handleShowHistory = () => {
    setShowHistory(true);
    loadPrintHistory();
  };
  
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/sfg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Printer className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold font-display">Main Label Printing</h1>
                <p className="text-sm text-[#E8DCC8]">Scan & Auto-Print via USB</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PrintMethodToggle screenKey="sfg-main-label" dark />
            {usbDevice ? (
              <Badge variant="outline" className="border-green-400 text-green-400">
                <Usb className="w-3 h-3 mr-1" />
                {usbDevice.productName || 'Connected'}
              </Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={connectUSBPrinter}
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                data-testid="connect-printer-btn"
              >
                <Usb className="w-3 h-3 mr-1" />
                Connect Printer
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowSettings(true)}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={handleShowHistory}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="print-history-btn"
            >
              <History className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8">
        {/* Session Stats */}
        <div className="flex justify-center gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{sessionStats.printed}</div>
            <div className="text-sm text-gray-500">Printed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{sessionStats.failed}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        </div>
        
        {/* Main Scan Card */}
        <Card className="mb-6 shadow-xl border-2 border-amber-200" data-testid="scan-bundle-card">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Zap className="w-6 h-6" />
              Scan Bundle to Print
            </CardTitle>
            <CardDescription className="text-amber-100">
              Template auto-selected from Item Master • Scan → Print!
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Pricing Type Selector */}
              <div className="flex items-center gap-4 mb-2">
                <Label className="text-sm font-medium whitespace-nowrap">Pricing Type:</Label>
                <Select value={pricingType} onValueChange={setPricingType}>
                  <SelectTrigger className="w-36" data-testid="pricing-type-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePricingTypes.map((pt) => (
                      <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-500">
                  {pricingType === 'WHT' ? '(QR2 = Barcode)' : '(QR2 = Optional Barcode)'}
                </span>
              </div>

              {isAdmin && (
                <label
                  className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 cursor-pointer"
                  data-testid="admin-override-toggle-label"
                >
                  <Checkbox
                    checked={adminOverride}
                    onCheckedChange={(v) => setAdminOverride(!!v)}
                    className="mt-0.5"
                    data-testid="admin-override-checkbox"
                  />
                  <span className="text-xs text-red-700 leading-snug">
                    <span className="font-semibold">Admin override:</span> print labels for a bundle in <span className="font-semibold">any status</span> (not just IN_WIP). Use only when necessary — the bundle may still be needed for picking.
                  </span>
                </label>
              )}
              
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (staffId.trim()) scanInputRef.current?.focus(); } }}
                placeholder="Scan / enter Staff ID (credited for these pieces)"
                className="h-12 text-lg text-center font-mono tracking-wider"
                disabled={isProcessing}
                data-testid="staff-id-input"
              />
              <p className="text-xs text-gray-500 -mt-1 text-center">
                This staff is credited with the bundle's pieces in Staff Rankings &amp; Wages.
              </p>

              <Input
                ref={scanInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleScanAndPrint()}
                placeholder="Scan bundle barcode here..."
                className="h-16 text-2xl text-center font-mono tracking-wider"
                disabled={isProcessing}
                autoFocus
                data-testid="bundle-code-input"
              />
              
              {isProcessing && (
                <div className="flex items-center justify-center gap-3 py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-amber-600" />
                  <span className="text-lg font-medium text-amber-700">{processingStatus}</span>
                </div>
              )}
              
              {!isProcessing && !usbDevice && (
                <Alert className="bg-yellow-50 border-yellow-300">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertTitle className="text-yellow-800">Printer Not Connected</AlertTitle>
                  <AlertDescription className="text-yellow-700">
                    Click "Connect Printer" in the header to connect your USB printer first.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Last Printed Bundle */}
        {lastPrintedBundle && (
          <Card className="mb-6 bg-green-50 border-green-200" data-testid="last-printed-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <div className="font-bold text-green-800 text-lg">
                    {lastPrintedBundle.bundle_code}
                  </div>
                  <div className="text-sm text-green-600">
                    {lastPrintedBundle.labels_printed} labels • {lastPrintedBundle.usn_count} USNs • Template: {lastPrintedBundle.templates_used?.join(', ') || 'Standard'}
                  </div>
                </div>
                <div className="text-xs text-green-500">
                  {lastPrintedBundle.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Instructions */}
        <Card className="shadow-sm bg-gray-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              How It Works
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
              <li>Connect USB printer (one-time)</li>
              <li>Select Pricing Type (default: TN)</li>
              <li>Scan bundle barcode</li>
              <li>Template auto-selected from Item Master</li>
              <li>Labels print automatically!</li>
            </ol>
            <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-700">
                <strong>QR Code 2:</strong> Shows <em>Optional Barcode</em> for all pricing types except WHT (which shows the main Barcode).
              </p>
            </div>
            <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
              <p className="text-xs text-orange-700">
                <strong>Note:</strong> For items with box templates, you'll be prompted to change the sticker roll before printing.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <PricingTypePrompt prompt={ratePrompt} onCancel={() => { ratePendingRef.current = false; setRatePrompt(null); setStaffId(''); }}
        onPick={(pt) => { const code = ratePrompt.code; ratePendingRef.current = false; setRatePrompt(null); setPricingType(pt); toast.info(`Printing ${code} with ${pt} rates`); handleScanAndPrint(pt, code); }} />
      {/* Combo/Box Sticker Alert Dialog */}
      <Dialog open={showComboAlert} onOpenChange={setShowComboAlert}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Product Labels Printed - Box Sticker Required
            </DialogTitle>
            <DialogDescription>
              Product labels have been printed. Now select the box label printer and print box labels.
            </DialogDescription>
          </DialogHeader>
          
          {pendingPrintData && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-800">
                  ✓ Product Labels Printed
                </div>
                <div className="text-sm text-green-600">
                  Bundle: {pendingPrintData.bundleData.bundle_code} • {pendingPrintData.bundleData.usn_count} USNs
                </div>
              </div>
              
              {/* Box Printer Selection */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-medium text-blue-800 mb-2">
                  Box Label Printer
                </div>
                {boxPrinterDevice ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <Printer className="w-4 h-4" />
                      <span>{boxPrinterDevice.productName || 'USB Printer'}</span>
                      <span className="text-green-600 font-medium">✓ Connected</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={connectBoxPrinter}
                      className="text-blue-600 border-blue-300"
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={connectBoxPrinter}
                    variant="outline"
                    className="w-full border-blue-300 text-blue-600 hover:bg-blue-100"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Select Box Label Printer
                  </Button>
                )}
              </div>
              
              <Alert className="bg-yellow-50 border-yellow-300">
                <Package className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">Ready to Print</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  {boxPrinterDevice 
                    ? 'Box label printer is connected. Click "Print Box Labels" to proceed.'
                    : 'Please select the printer with box sticker roll loaded.'}
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleComboCancel}>
              Skip Box Labels
            </Button>
            <Button 
              onClick={handleComboConfirm} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!boxPrinterDevice}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Box Labels
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Printer Settings
            </DialogTitle>
            <DialogDescription>
              Template is auto-selected from Item Master
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Info about auto-template */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Template:</strong> Automatically selected based on the item's <code>print_template</code> field in Item Master.
              </p>
            </div>
            
            {/* Printer Model */}
            <div className="space-y-2">
              <Label>Printer Model</Label>
              <Select value={printerModel} onValueChange={setPrinterModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRINTER_CONFIGS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Copies per Label */}
            <div className="space-y-2">
              <Label>Copies per Label</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={copiesPerLabel}
                onChange={(e) => setCopiesPerLabel(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
            
            {/* USB Status */}
            <div className="p-3 bg-gray-100 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">USB Printer</span>
                {usbDevice ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    {usbDevice.productName || 'Connected'}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={connectUSBPrinter}>
                    <Usb className="w-3 h-3 mr-1" />
                    Connect
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)} className="w-full">
              <CheckCircle className="w-4 h-4 mr-2" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Print History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Print History
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bundle Code</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>USNs</TableHead>
                  <TableHead>Printed At</TableHead>
                  <TableHead>Printed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {printHistory.map((item) => (
                  <TableRow key={item.bundle_code}>
                    <TableCell className="font-mono">{item.bundle_code}</TableCell>
                    <TableCell>{item.source_section}</TableCell>
                    <TableCell>{item.printed_usn_count || item.usn_count}</TableCell>
                    <TableCell>{new Date(item.label_printed_at).toLocaleString()}</TableCell>
                    <TableCell>{item.label_printed_by_name}</TableCell>
                  </TableRow>
                ))}
                {printHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No print history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SFGMainLabelPrinting;
